/**
 * M.C.A. (Marta Calendar Assistant) Configuration
 *
 * Agent for calendar intelligence, scheduling and reminders
 */

import type { AIAgent } from '../types.js';

export const MCA_AGENT_ID = 'mca';
export const MCA_AGENT_VERSION = '1.0.0';

export const MCA_AGENT: AIAgent = {
  id: MCA_AGENT_ID,
  name: 'Marta Calendar Assistant',
  description: 'Manages calendar events, detects conflicts, suggests reminders and optimizes scheduling',
  version: MCA_AGENT_VERSION,
  enabled: true,

  capabilities: [
    {
      id: 'detect-conflicts',
      name: 'Detect Scheduling Conflicts',
      description: 'Checks for overlapping events in a given time range',
      inputSchema: {
        type: 'object',
        properties: {
          startDate: { type: 'string', description: 'Start datetime ISO' },
          endDate: { type: 'string', description: 'End datetime ISO' },
          excludeEventId: { type: 'string', description: 'Event ID to exclude from check' },
        },
        required: ['startDate', 'endDate'],
      },
      outputSchema: {
        type: 'object',
        properties: {
          hasConflicts: { type: 'boolean' },
          conflicts: { type: 'array', items: { type: 'object' } },
          suggestedSlots: { type: 'array', items: { type: 'string' } },
        },
      },
    },
    {
      id: 'suggest-reminders',
      name: 'Suggest Reminders',
      description: 'Analyzes upcoming deadlines and suggests reminders for tasks and milestones',
      inputSchema: {
        type: 'object',
        properties: {
          daysAhead: { type: 'number', description: 'Days to look ahead (default 7)' },
        },
      },
      outputSchema: {
        type: 'object',
        properties: {
          reminders: { type: 'array', items: { type: 'object' } },
          count: { type: 'number' },
        },
      },
    },
    {
      id: 'daily-briefing',
      name: 'Daily Briefing',
      description: 'Generates a summary of today\'s schedule with preparation notes',
      inputSchema: {
        type: 'object',
        properties: {
          date: { type: 'string', description: 'Date for briefing (default today)' },
        },
      },
      outputSchema: {
        type: 'object',
        properties: {
          date: { type: 'string' },
          eventCount: { type: 'number' },
          events: { type: 'array' },
          summary: { type: 'string' },
        },
      },
    },
    {
      id: 'find-free-slots',
      name: 'Find Free Slots',
      description: 'Finds available time slots for scheduling new meetings',
      inputSchema: {
        type: 'object',
        properties: {
          durationMinutes: { type: 'number', description: 'Desired meeting duration' },
          dateRange: { type: 'string', description: 'Date range to search (e.g. "next week")' },
          workingHoursOnly: { type: 'boolean', description: 'Only within 9-18 (default true)' },
        },
        required: ['durationMinutes'],
      },
      outputSchema: {
        type: 'object',
        properties: {
          slots: { type: 'array', items: { type: 'object' } },
          count: { type: 'number' },
        },
      },
    },
  ],

  workflows: ['daily-briefing', 'deadline-reminder', 'conflict-detection'],
  triggers: [
    'entity.created/calendar_event',
    'entity.updated/calendar_event',
    'entity.deleted/calendar_event',
    'entity.created/task',
    'entity.updated/task',
    'entity.created/milestone',
  ],

  metrics: { totalCalls: 0, successRate: 0, avgLatencyMs: 0, correctionRate: 0 },
  trainingDataCount: 0,
  lastTrainedAt: undefined,
};

export const MCA_SETTINGS = {
  model: 'llama-3.3-70b-versatile',
  maxTokens: 400,
  workingHoursStart: 9,
  workingHoursEnd: 18,
  defaultReminderMinutes: 15,
  conflictBufferMinutes: 5,
  briefingHour: 8,
};
